/**
 * RoadHazardDialog - Report road hazards
 * 
 * Allows users to report hazards: crash, slowdown, construction, lane closure,
 * object on road, flooded road, low visibility, and cops.
 * 
 * Cop reports become "ghost mode" checkpoints after 30 minutes - the cop may
 * have left but the checkpoint location remains as a warning for another 30 min.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Warning as HazardIcon,
  MyLocation as LocationIcon,
  Send as SendIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { telegram } from '../services/telegram';
import { feedbackService } from '../services/feedback';
import { NavigationService } from '../services/navigation';
import { requestPreciseLocation } from '../services/highPrecisionLocation';

// Material UI style hazard icons with dark backgrounds
const hazardIcons = {
  crash: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23ef4444' stroke-width='1.5'/><path d='M28 18L22 32h5l-2 8 10-16h-5l3-6z' fill='%23ef4444'/></svg>`,
  slowdown: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23f59e0b' stroke-width='1.5'/><circle cx='28' cy='28' r='12' fill='none' stroke='%23f59e0b' stroke-width='3'/><path d='M28 16v24M40 28H16' stroke='%23f59e0b' stroke-width='2' stroke-linecap='round'/></svg>`,
  construction: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23f97316' stroke-width='1.5'/><path d='M16 36h24M18 32l2-12 4 12 4-12 4 12 4-12 2 12M16 38h24v2H16z' fill='%23f97316'/></svg>`,
  lane_closure: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%238b5cf6' stroke-width='1.5'/><path d='M28 16v24M20 24h16M20 32h16' stroke='%238b5cf6' stroke-width='3' stroke-linecap='round'/><circle cx='28' cy='28' r='14' fill='none' stroke='%238b5cf6' stroke-width='2'/><path d='M20 20l16 16M36 20l-16 16' stroke='%238b5cf6' stroke-width='2'/></svg>`,
  object_on_road: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23eab308' stroke-width='1.5'/><path d='M28 16l-8 24h16l-8-24z' fill='none' stroke='%23eab308' stroke-width='2'/><circle cx='28' cy='24' r='2' fill='%23eab308'/><path d='M28 28v6' stroke='%23eab308' stroke-width='2' stroke-linecap='round'/></svg>`,
  flooded_road: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%230ea5e9' stroke-width='1.5'/><path d='M16 28q3-2 6 0t6 0 6 0 6 0M16 34q3-2 6 0t6 0 6 0 6 0' stroke='%230ea5e9' stroke-width='2' fill='none'/><path d='M22 22q2-3 4 0t4 0M26 18q1-2 2 0t2 0' stroke='%230ea5e9' stroke-width='1.5' fill='none'/></svg>`,
  low_visibility: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%2364748b' stroke-width='1.5'/><path d='M20 28q2-4 8-4t8 4M24 32q1-2 4-2t4 2' stroke='%2364748b' stroke-width='2' fill='none'/><circle cx='28' cy='28' r='3' fill='%2364748b'/><path d='M18 22q3-2 10-2t10 2' stroke='%2364748b' stroke-width='1.5' fill='none' opacity='0.5'/></svg>`,
  cop: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%233b82f6' stroke-width='1.5'/><path d='M28 18l-2 4-4 1 3 3-1 4 4-2 4 2-1-4 3-3-4-1z' fill='%233b82f6'/><rect x='22' y='26' width='12' height='10' rx='1' fill='none' stroke='%233b82f6' stroke-width='2'/></svg>`,
  safety_inspector: `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='56' height='56' viewBox='0 0 56 56'><circle cx='28' cy='28' r='26' fill='%231f1f1f' stroke='%23ec4899' stroke-width='1.5'/><path d='M28 18l-2 4-4 1 3 3-1 4 4-2 4 2-1-4 3-3-4-1z' fill='%23ec4899'/><path d='M22 30c-2 2-1 6 6 8 7-2 8-6 6-8' fill='%23ec4899'/></svg>`,
};

interface RoadHazardDialogProps {
  open: boolean;
  onClose: () => void;
  onHazardReported?: (hazard: HazardReport) => void;
  currentLocation?: { lat: number; lng: number } | null;
}

interface HazardReport {
  id: string;
  type: string;
  latitude: number;
  longitude: number;
  note: string | null;
  is_ghost: boolean;
  created_at: string;
  expires_at: string;
}

interface HazardType {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  duration: string;
  durationMs: number;
  description: string;
}

const HAZARD_TYPES: HazardType[] = [
  {
    id: 'crash',
    label: 'Crash',
    icon: <Box component="img" src={hazardIcons.crash} sx={{ width: 40, height: 40 }} />,
    color: '#ef4444',
    duration: '3 hours',
    durationMs: 3 * 60 * 60 * 1000,
    description: 'Vehicle accident or collision on the road',
  },
  {
    id: 'slowdown',
    label: 'Slowdown',
    icon: <Box component="img" src={hazardIcons.slowdown} sx={{ width: 40, height: 40 }} />,
    color: '#f59e0b',
    duration: '1 hour',
    durationMs: 1 * 60 * 60 * 1000,
    description: 'Traffic backup or slow moving vehicles',
  },
  {
    id: 'construction',
    label: 'Construction',
    icon: <Box component="img" src={hazardIcons.construction} sx={{ width: 40, height: 40 }} />,
    color: '#f97316',
    duration: '8 hours',
    durationMs: 8 * 60 * 60 * 1000,
    description: 'Road work or construction zone',
  },
  {
    id: 'lane_closure',
    label: 'Lane Closure',
    icon: <Box component="img" src={hazardIcons.lane_closure} sx={{ width: 40, height: 40 }} />,
    color: '#8b5cf6',
    duration: '4 hours',
    durationMs: 4 * 60 * 60 * 1000,
    description: 'One or more lanes blocked',
  },
  {
    id: 'object_on_road',
    label: 'Object on Road',
    icon: <Box component="img" src={hazardIcons.object_on_road} sx={{ width: 40, height: 40 }} />,
    color: '#eab308',
    duration: '1 hour',
    durationMs: 1 * 60 * 60 * 1000,
    description: 'Debris, tire, animal, or obstacle in roadway',
  },
  {
    id: 'flooded_road',
    label: 'Flooded Road',
    icon: <Box component="img" src={hazardIcons.flooded_road} sx={{ width: 40, height: 40 }} />,
    color: '#0ea5e9',
    duration: '6 hours',
    durationMs: 6 * 60 * 60 * 1000,
    description: 'Water covering roadway - turn around, don\'t drown',
  },
  {
    id: 'low_visibility',
    label: 'Low Visibility',
    icon: <Box component="img" src={hazardIcons.low_visibility} sx={{ width: 40, height: 40 }} />,
    color: '#64748b',
    duration: '2 hours',
    durationMs: 2 * 60 * 60 * 1000,
    description: 'Fog, dust storm, smoke, or heavy rain',
  },
  {
    id: 'cop',
    label: 'Cop',
    icon: <Box component="img" src={hazardIcons.cop} sx={{ width: 40, height: 40 }} />,
    color: '#3b82f6',
    duration: '30min + ghost',
    durationMs: 30 * 60 * 1000,
    description: 'Law enforcement spotted - becomes checkpoint after 30 min',
  },
  {
    id: 'safety_inspector',
    label: 'Safety Inspector',
    icon: <Box component="img" src={hazardIcons.safety_inspector} sx={{ width: 40, height: 40 }} />,
    color: '#ec4899',
    duration: '30min + ghost',
    durationMs: 30 * 60 * 1000,
    description: 'Safety inspector spotted - work site checkpoint',
  },
];

const RoadHazardDialog: React.FC<RoadHazardDialogProps> = ({
  open,
  onClose,
  onHazardReported,
  currentLocation,
}) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    currentLocation || null
  );

  // Auto-fetch location when dialog opens if not already provided
  useEffect(() => {
    if (open && !location && !gettingLocation) {
      handleGetLocation();
    }
  }, [open]);

  const handleGetLocation = async () => {
    setGettingLocation(true);
    feedbackService.tap();

    try {
      // Try NavigationService first (has cached location)
      const navLocation = await NavigationService.getCurrentLocation();
      if (navLocation) {
        setLocation({
          lat: navLocation.lat,
          lng: navLocation.lng,
        });
        telegram.hapticFeedback('success');
        return;
      }
    } catch (navError) {
      console.warn('NavigationService location failed, trying precise service:', navError);
    }

    try {
      const result = await requestPreciseLocation({
        desiredAccuracyMeters: 15,
        refinementTimeoutMs: 7000,
        allowLastKnownFallback: true,
        allowTelegramFallback: true,
      });

      if (!result.fix) {
        const reason = result.reason || 'Could not get your location. Please enable GPS and try again.';
        telegram.showAlert(reason);
        telegram.hapticFeedback('error');
        return;
      }

      setLocation({ lat: result.fix.lat, lng: result.fix.lng });
      telegram.hapticFeedback('success');

      if (!result.fulfilled && typeof result.fix.accuracy === 'number') {
        telegram.showAlert(`⚠️ Location accuracy is ±${Math.round(result.fix.accuracy)}m. If this feels off, try again.`);
      }
    } catch (error) {
      console.error('Precise location error:', error);
      telegram.hapticFeedback('error');
      const message = error instanceof Error ? error.message : 'Unable to get location.';
      telegram.showAlert(message);
    } finally {
      setGettingLocation(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !location) return;

    setSubmitting(true);
    feedbackService.tap();

    try {
      const hazardType = HAZARD_TYPES.find((t) => t.id === selectedType);
      if (!hazardType) throw new Error('Invalid hazard type');

      // Submit to API
      const response = await fetch('/api/hazards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType,
          latitude: location.lat,
          longitude: location.lng,
          note: note.trim() || null,
          reporterId: telegram.getUserId(),
          reporterName: telegram.getUser()?.first_name || 'Anonymous',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit hazard');
      }

      const data = await response.json();
      
      // Notify parent component for snackbar broadcast
      if (onHazardReported && data.hazard) {
        onHazardReported(data.hazard);
      }

      setSubmitted(true);
      feedbackService.playSound('success');
      telegram.hapticFeedback('success');

      // Close after delay
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to submit hazard:', err);
      
      // Store locally if API fails
      const hazards = JSON.parse(localStorage.getItem('pending_hazards') || '[]');
      const hazardType = HAZARD_TYPES.find((t) => t.id === selectedType);
      const localHazard = {
        id: `local_${Date.now()}`,
        type: selectedType,
        latitude: location.lat,
        longitude: location.lng,
        note: note.trim() || null,
        is_ghost: false,
        created_at: new Date().toISOString(),
        expires_at: hazardType && hazardType.durationMs > 0
          ? new Date(Date.now() + hazardType.durationMs).toISOString()
          : new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      };
      hazards.push(localHazard);
      localStorage.setItem('pending_hazards', JSON.stringify(hazards));
      
      // Still notify for local display
      if (onHazardReported) {
        onHazardReported(localHazard);
      }
      
      setSubmitted(true);
      feedbackService.playSound('success');
      
      setTimeout(() => {
        handleClose();
      }, 2000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedType(null);
      setNote('');
      setSubmitted(false);
      setLocation(currentLocation || null);
      onClose();
    }
  };

  const selectedHazard = HAZARD_TYPES.find((t) => t.id === selectedType);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(239, 68, 68, 0.2)',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HazardIcon sx={{ color: '#ef4444' }} />
          <Typography
            variant="h6"
            sx={{
              background: 'linear-gradient(90deg, #ef4444, #f59e0b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            Report Road Hazard
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={submitting}
          sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: '#ef4444' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {submitted ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              animation: 'fadeIn 0.3s ease-out',
              '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'scale(0.9)' },
                to: { opacity: 1, transform: 'scale(1)' },
              },
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${selectedHazard?.color}20, ${selectedHazard?.color}40)`,
                border: `3px solid ${selectedHazard?.color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                '& svg': { fontSize: 40, color: selectedHazard?.color },
              }}
            >
              {selectedHazard?.icon}
            </Box>
            <Typography variant="h6" sx={{ color: selectedHazard?.color, fontWeight: 600, mb: 1 }}>
              Hazard Reported!
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
              Thank you for helping keep other drivers safe.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Location Status */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                mb: 3,
                background: location
                  ? 'rgba(16, 185, 129, 0.1)'
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${location ? '#10b981' : '#ef4444'}`,
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon sx={{ color: location ? '#10b981' : '#ef4444' }} />
                <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                  {location
                    ? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`
                    : 'Location required'}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={handleGetLocation}
                disabled={gettingLocation}
                sx={{
                  color: '#06b6d4',
                  '&:hover': { background: 'rgba(6, 182, 212, 0.1)' },
                }}
              >
                {gettingLocation ? (
                  <CircularProgress size={20} sx={{ color: '#06b6d4' }} />
                ) : location ? (
                  'Update'
                ) : (
                  'Get Location'
                )}
              </Button>
            </Box>

            {/* Hazard Type Selection */}
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, fontSize: '0.9rem' }}>
              What type of hazard?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {HAZARD_TYPES.map((type) => (
                <Box
                  key={type.id}
                  onClick={() => {
                    setSelectedType(type.id);
                    feedbackService.tap();
                    telegram.hapticFeedback('light');
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    cursor: 'pointer',
                    background: selectedType === type.id
                      ? `${type.color}15`
                      : 'rgba(255, 255, 255, 0.02)',
                    border: `2px solid ${selectedType === type.id ? type.color : 'rgba(255, 255, 255, 0.1)'}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: `${type.color}10`,
                      borderColor: `${type.color}50`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${type.color}20`,
                      mr: 2,
                      '& svg': { fontSize: 24, color: type.color },
                    }}
                  >
                    {type.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: 'white', fontWeight: 600 }}>
                        {type.label}
                      </Typography>
                      <Chip
                        label={type.duration}
                        size="small"
                        sx={{
                          background: `${type.color}30`,
                          color: type.color,
                          fontSize: '0.7rem',
                          height: 20,
                        }}
                      />
                    </Box>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                      {type.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Optional Note */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Details (optional)"
              placeholder="e.g., 'Northbound lane only', 'Near mile marker 42'"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(239, 68, 68, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#ef4444' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.5)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#ef4444' },
              }}
            />

            {/* Info Box */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1.5,
                mb: 3,
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: 1,
              }}
            >
              <InfoIcon sx={{ color: '#3b82f6', fontSize: 18, mt: 0.3 }} />
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                Other drivers can vote to confirm or dismiss hazards. Reports with
                multiple confirmations stay longer, while disputed reports are removed faster.
              </Typography>
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={submitting || !selectedType || !location}
              sx={{
                py: 1.5,
                background: selectedHazard
                  ? `linear-gradient(135deg, ${selectedHazard.color}, ${selectedHazard.color}99)`
                  : 'linear-gradient(135deg, #ef4444, #dc2626)',
                fontWeight: 600,
                '&:hover': {
                  background: selectedHazard
                    ? `linear-gradient(135deg, ${selectedHazard.color}dd, ${selectedHazard.color}77)`
                    : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              {submitting ? 'Reporting...' : 'Report Hazard'}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoadHazardDialog;
